import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home as HomeIcon, UtensilsCrossed, ShoppingBag, Gift, Menu as MenuIcon, X, LogOut } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const Logo = () => (
  <div className="h-10 w-10 rounded-full bg-gradient-to-b from-[#c79a4a] to-[#a97e33] flex items-center justify-center shadow ring-2 ring-[#8f6a2a]/40 shrink-0">
    <div className="text-center leading-none">
      <div className="text-[6px] font-semibold tracking-widest text-[#3a2519]">TABLE</div>
      <div className="text-[12px] font-extrabold text-[#3a2519] -mt-0.5">24</div>
    </div>
  </div>
);

const links = [
  { to: "/", label: "Home", icon: HomeIcon },
  { to: "/menu", label: "Daily Food", icon: UtensilsCrossed },
  { to: "/sweets", label: "Sweets", icon: Gift },
  { to: "/catering", label: "Catering", icon: UtensilsCrossed },
];

const Navbar = () => {
  const { count } = useCart();
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const isActive = (to) => (to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to));

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50 bg-[#f7efe1]/95 backdrop-blur border-b border-[#e6d8bf]">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Logo />
            <span className="font-heading text-xl font-bold text-[#2c1e12] hidden sm:block">TABLE24</span>
          </Link>

          <nav className="hidden md:flex items-center gap-7">
            {links.map((l) => (
              <Link key={l.to} to={l.to}
                className={`text-[15px] font-medium transition-colors ${isActive(l.to) ? "text-[#9a4a2f]" : "text-[#4a3524] hover:text-[#9a4a2f]"}`}>
                {l.label}
              </Link>
            ))}
            <Link to="/rewards" className={`text-[15px] font-medium transition-colors ${isActive("/rewards") ? "text-[#9a4a2f]" : "text-[#4a3524] hover:text-[#9a4a2f]"}`}>Rewards</Link>
          </nav>

          <div className="flex items-center gap-2">
            <Link to="/cart" className="relative p-2 text-[#4a3524] hover:text-[#9a4a2f]">
              <ShoppingBag className="h-5 w-5" />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-[#9a4a2f] text-white text-[10px] font-bold flex items-center justify-center">{count}</span>
              )}
            </Link>
            {user ? (
              <div className="hidden md:flex items-center gap-2">
                <Link to="/rewards" className="text-sm font-medium text-[#4a3524] hover:text-[#9a4a2f]">Hi, {user.first_name}</Link>
                <button onClick={() => { logout(); nav("/"); }} className="p-2 text-[#4a3524] hover:text-[#9a4a2f]" aria-label="Log out"><LogOut className="h-4 w-4" /></button>
              </div>
            ) : (
              <Link to="/auth" className="hidden md:inline-flex rounded-md bg-[#9a4a2f] hover:bg-[#853e26] text-white text-sm font-medium px-4 py-2 transition-colors">Sign In</Link>
            )}
            <button className="md:hidden p-2 text-[#4a3524]" onClick={() => setOpen((o) => !o)} aria-label="Menu">
              {open ? <X className="h-6 w-6" /> : <MenuIcon className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="md:hidden bg-[#f7efe1] border-t border-[#e6d8bf] px-5 py-3">
            {[...links, { to: "/rewards", label: "Rewards" }].map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block py-2 text-[#4a3524] font-medium">{l.label}</Link>
            ))}
            {user ? (
              <button onClick={() => { logout(); setOpen(false); nav("/"); }} className="mt-1 text-[#9a4a2f] font-medium">Log out ({user.first_name})</button>
            ) : (
              <Link to="/auth" onClick={() => setOpen(false)} className="mt-2 inline-flex rounded-md bg-[#9a4a2f] text-white text-sm font-medium px-4 py-2">Sign In</Link>
            )}
          </div>
        )}
      </header>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-[#f7efe1] border-t border-[#e6d8bf] flex">
        {[{ to: "/", label: "Home", icon: HomeIcon }, { to: "/menu", label: "Menu", icon: UtensilsCrossed }, { to: "/cart", label: "Cart", icon: ShoppingBag }, { to: "/rewards", label: "Rewards", icon: Gift }].map((l) => {
          const Icon = l.icon;
          const active = isActive(l.to);
          return (
            <Link key={l.to} to={l.to} className={`flex-1 flex flex-col items-center py-2 gap-0.5 ${active ? "text-[#9a4a2f]" : "text-[#8a7458]"}`}>
              <div className="relative">
                <Icon className="h-5 w-5" />
                {l.to === "/cart" && count > 0 && (
                  <span className="absolute -top-1.5 -right-2 h-4 min-w-4 px-1 rounded-full bg-[#9a4a2f] text-white text-[9px] font-bold flex items-center justify-center">{count}</span>
                )}
              </div>
              <span className="text-[10px] font-medium">{l.label}</span>
            </Link>
          );
        })}
      </nav>
    </>
  );
};

export default Navbar;
